import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  AUDIENCE_BANDS,
  bandLabel,
  type AudienceBand,
} from "@shared/plan-matrix";
import { portalApi } from "../api";
import { errorText } from "../errors";
import { useI18n } from "../i18n";
import type {
  CatalogueTitle,
  Lang,
  OrgPlanView,
  UpgradeOption,
} from "../types";
import { Link, Loading, Notice, PageTitle, Poster } from "../ui";
import { ViewersSection } from "./Viewers";

/**
 * "Er plan" - the purchaser's buy flow (ORG-5,
 * `docs/design/organisation-plans.md`).
 *
 * 1. What the organisation has: the plan, its price and renewal date.
 * 2. Its films: the ones chosen, and - on a plan that counts films - a
 *    picker over the catalogue, capped at what is left. A choice is
 *    PERMANENT, so the button says so before it is pressed.
 * 3. Upgrades: every higher plan, with what it costs NOW (the yearly
 *    difference for the rest of the plan year). There is no way down.
 */

const MAX_PAGES = 20;

const PLAN_ERRORS = {
  too_many: "plan.err.tooMany",
  not_in_catalogue: "plan.err.notInCatalogue",
  full_library: "plan.err.fullLibrary",
  not_an_upgrade: "plan.err.notUpgrade",
  not_sold: "plan.err.notSold",
  not_purchaser: "plan.err.notPurchaser",
  no_plan: "plan.err.noPlan",
} as const;

function kr(amount: number, lang: Lang): string {
  return new Intl.NumberFormat(lang === "sv" ? "sv-SE" : "en-GB", {
    style: "currency",
    currency: "SEK",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

function day(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function filmCount(n: number, t: ReturnType<typeof useI18n>["t"]): string {
  return n === 1 ? t("plan.film1") : t("plan.filmsN", { n });
}

async function wholeCatalogue(lang: Lang): Promise<CatalogueTitle[]> {
  const out: CatalogueTitle[] = [];
  let page: number | null = 1;
  for (let n = 0; page !== null && n < MAX_PAGES; n++) {
    const res = await portalApi.titles({ page, pageSize: 48, lang });
    out.push(...res.titles);
    page = res.pagination.next;
  }
  return out;
}

export function PlanScreen() {
  const { t, lang } = useI18n();
  const [view, setView] = useState<OrgPlanView | null>(null);
  const [titles, setTitles] = useState<CatalogueTitle[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await portalApi.org());
    } catch (err) {
      setFailed(errorText(err, t, PLAN_ERRORS));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let live = true;
    wholeCatalogue(lang)
      .then((list) => live && setTitles(list))
      .catch(() => live && setTitles([]));
    return () => {
      live = false;
    };
  }, [lang]);

  const byId = useMemo(
    () => new Map((titles ?? []).map((x) => [x.assetId, x])),
    [titles]
  );

  if (failed) return <Notice kind="error">{failed}</Notice>;
  if (!view) return <Loading />;

  const picked = new Set(view.picks.map((p) => p.assetId));
  const left = view.picksLeft;
  const canPick = left !== null && left > 0;
  const q = filter.trim().toLowerCase();
  const candidates = (titles ?? []).filter(
    (x) => !picked.has(x.assetId) && (!q || x.title.toLowerCase().includes(q))
  );

  const toggle = (id: string) =>
    setChosen((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : left !== null && prev.length < left
          ? [...prev, id]
          : prev
    );

  async function savePicks() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setView(await portalApi.orgPick(chosen));
      setNotice(t("plan.picked"));
      setChosen([]);
    } catch (err) {
      setError(errorText(err, t, PLAN_ERRORS));
      await load();
    } finally {
      setBusy(false);
    }
  }

  const p = view.plan;

  return (
    <div className="plan-page">
      <PageTitle>{t("plan.title")}</PageTitle>
      {notice && <Notice kind="success">{notice}</Notice>}
      {error && <Notice kind="error">{error}</Notice>}

      <PlanSummary view={view} />

      <section className="panel" aria-labelledby="plan-films">
        <h2 id="plan-films">
          {p.films === null
            ? t("plan.filmsTitleAll")
            : t("plan.filmsTitle", { picked: view.picks.length, of: p.films })}
        </h2>
        {p.films === null ? (
          <p>{t("plan.fullLibraryHint")}</p>
        ) : view.picks.length === 0 ? (
          <p>{t("plan.noPicks")}</p>
        ) : (
          <ul className="plan-picks">
            {view.picks.map((pick) => {
              const title = byId.get(pick.assetId);
              return (
                <li key={pick.assetId}>
                  <Link to={`/titles/${encodeURIComponent(pick.assetId)}`}>
                    {title?.title ?? pick.assetId}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {canPick && (
          <div className="plan-picker">
            <h3>{t("plan.pickTitle", { n: left })}</h3>
            <p className="field__hint">{t("plan.pickHint")}</p>
            <div className="field">
              <label htmlFor="plan-filter">{t("plan.filter")}</label>
              <input
                id="plan-filter"
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            {titles === null ? (
              <Loading />
            ) : (
              <ul className="plan-picker__grid">
                {candidates.map((x) => {
                  const on = chosen.includes(x.assetId);
                  const full = !on && chosen.length >= (left ?? 0);
                  return (
                    <li key={x.assetId}>
                      <label
                        className={`plan-pick${on ? " plan-pick--on" : ""}${full ? " plan-pick--full" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={full}
                          onChange={() => toggle(x.assetId)}
                        />
                        <Poster
                          src={x.images.landscape ?? x.images.banner}
                          title={x.title}
                          shape="landscape"
                        />
                        <span className="plan-pick__title">{x.title}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="plan-picker__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || chosen.length === 0}
                onClick={() => void savePicks()}
              >
                {t("plan.pickSave", { n: chosen.length })}
              </button>
              <span className="field__hint">{t("plan.pickPermanent")}</span>
            </div>
          </div>
        )}
      </section>

      <ViewersSection />

      <UpgradeSection
        view={view}
        onUpgraded={(next, label) => {
          setView(next);
          setError(null);
          setNotice(t("plan.upgraded", { plan: label }));
        }}
      />
    </div>
  );
}

/** What the organisation has: name, package, price and renewal date. */
export function PlanSummary({ view }: { view: OrgPlanView }) {
  const { t, lang } = useI18n();
  const p = view.plan;
  const films =
    p.films === null ? t("apply.fullLibrary") : filmCount(p.films, t);
  return (
    <section className="panel plan-summary" aria-labelledby="plan-current">
      <h2 id="plan-current" className="plan-summary__name">
        {view.account.name}
      </h2>
      <p className="plan-summary__plan">
        {p.label} · {films} · {bandLabel(p.band, lang)}
      </p>
      <p>
        {t("plan.price", {
          price: kr(p.yearlyPrice, lang),
          renews: day(p.renewsAt, lang),
        })}
      </p>
    </section>
  );
}

/**
 * The upsell, asked the way the application asks (Sigge 2026-10-07): how
 * many watch one screening, then how many films. Only combinations that are
 * an upgrade are offered - the bands with at least one, and for the chosen
 * band its larger packages - each with its yearly price and what switching
 * costs NOW (the yearly difference for the rest of the subscription year).
 * Confirmed before it is bought; there is no way down. Used on "Ert
 * abonnemang" and on the purchaser's Konto page.
 */
export function UpgradeSection(props: {
  view: OrgPlanView;
  onUpgraded(next: OrgPlanView, label: string): void;
}) {
  const { t, lang } = useI18n();
  const { upgrades, plan } = props.view;
  const bands = AUDIENCE_BANDS.filter((b) =>
    upgrades.some((u) => u.band === b)
  );
  const [band, setBand] = useState<AudienceBand | null>(
    bands.includes(plan.band) ? plan.band : (bands[0] ?? null)
  );
  const [choice, setChoice] = useState<UpgradeOption | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const radios = useId();

  const levels = band ? upgrades.filter((u) => u.band === band) : [];

  async function upgrade(option: UpgradeOption) {
    setBusy(true);
    setError(null);
    try {
      const next = await portalApi.orgUpgrade(option.band, option.levelId);
      setChoice(null);
      props.onUpgraded(next, option.label);
    } catch (err) {
      setError(errorText(err, t, PLAN_ERRORS));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="plan-upgrades">
      <h2 id="plan-upgrades">{t("plan.upgradeTitle")}</h2>
      {error && <Notice kind="error">{error}</Notice>}
      {upgrades.length === 0 ? (
        <p>{t("plan.noUpgrades")}</p>
      ) : (
        <>
          <p className="field__hint">{t("plan.upgradeHint")}</p>
          <fieldset className="choice">
            <legend>{t("apply.band")}</legend>
            {bands.map((b) => (
              <label key={b} className="choice__option">
                <input
                  type="radio"
                  name={`${radios}-band`}
                  value={b}
                  checked={band === b}
                  onChange={() => {
                    setBand(b);
                    setChoice(null);
                    setConfirming(false);
                  }}
                />
                <span className="choice__text">
                  <span className="choice__label">{bandLabel(b, lang)}</span>
                  {b === plan.band && (
                    <span className="choice__price">
                      {t("plan.currentBand")}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
          {band && (
            <fieldset className="choice">
              <legend>{t("apply.level")}</legend>
              {levels.map((u) => (
                <label key={u.levelId} className="choice__option">
                  <input
                    type="radio"
                    name={`${radios}-level`}
                    value={u.levelId}
                    checked={choice === u}
                    onChange={() => {
                      setChoice(u);
                      setConfirming(false);
                    }}
                  />
                  <span className="choice__text">
                    <span className="choice__label">
                      {u.label} ·{" "}
                      {u.films === null
                        ? t("apply.fullLibrary")
                        : filmCount(u.films, t)}
                    </span>
                    <span className="choice__price">
                      {t("plan.upgradePrice", {
                        yearly: kr(u.yearlyPrice, lang),
                        now: kr(u.chargeNow, lang),
                      })}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          {choice &&
            (confirming ? (
              <div className="plan-upgrade__confirm">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={busy}
                  onClick={() => void upgrade(choice)}
                >
                  {t("plan.upgradeConfirm", {
                    now: kr(choice.chargeNow, lang),
                  })}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setConfirming(false)}
                >
                  {t("common.cancel")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                {t("plan.upgradeTo", { plan: choice.label })}
              </button>
            ))}
        </>
      )}
    </section>
  );
}
